import React, { useEffect, useMemo } from "react";
import { MultiSelectDropdown } from "./FormFields";
import { useCampusBuildingsList } from "../../query/hooks/QueryHooksCampusBuilding";
import { mapBuildingsToSelect } from "../../utils/CampusBuildingsUtil";

/**
 * A reusable multi‑select component for campus buildings.
 *
 * @component
 * @param {Object} props
 * @param {number|string} props.campusId - ID of the campus to fetch buildings for.
 * @param {Array} props.value - Currently selected building objects.
 * @param {Function} props.onChange - Callback when selection changes.
 * @param {string} [props.label="Buildings"] - Label for the field.
 * @param {string} [props.name="buildings"] - Field name (used for forms).
 * @param {boolean} [props.required=false] - Whether the field is required.
 * @param {number} [props.col_md=12] - Bootstrap medium column width.
 * @param {number} [props.col_lg=12] - Bootstrap large column width.
 * @param {boolean} [props.isDisabled] - Forces the field disabled, overriding the loading/no-options checks below.
 * @param {boolean} [props.filterSameCampus=true] - When true, selecting a building hides its sibling buildings (same campus_id) from the options.
 * @param {boolean} [props.isInvalid=false] - Marks the field invalid.
 * @param {string} [props.error] - Validation message rendered under the dropdown.
 * @param {Object} [props.selectStyles] - react-select style overrides handed to MultiSelectDropdown.
 * @param {string} [props.wrapperClass] - Extra classes for the Bootstrap column wrapper, e.g. `px-0` to drop its 15px gutter.
 * @param {Array<number>} [props.excludeBuildingIds] - buildings.id values to hide from the options, for callers that need a subset of buildings.
 * @returns {JSX.Element}
 */
const CampusBuildingMultiSelect = ({
    campusId,
    value = [],
    onChange,
    label = "Buildings",
    name = "buildings",
    required = false,
    col_md = 12,
    col_lg = 12,
    isDisabled,
    filterSameCampus = true,
    isInvalid = false,
    error,
    selectStyles,
    wrapperClass,
    excludeBuildingIds = [],
}) => {
    const { data: campusBuildingDetails, isLoading } = useCampusBuildingsList(campusId);

    const buildingOptions = useMemo(
        () => mapBuildingsToSelect(campusBuildingDetails).filter((building) => !excludeBuildingIds.includes(building.value)),
        [campusBuildingDetails, excludeBuildingIds]
    );

    /**
     * Purpose:
     * The deleted buildings must be removed from the value (values that are not present in buildingOptions).
     */
    useEffect(() => {
        if (!value?.length || !buildingOptions || !buildingOptions.length) {
            return;
        }

        const validBuildingIds = new Set(
            buildingOptions.map(option => option.value)
        );

        const filteredValue = value.filter(item =>
            validBuildingIds.has(item.value)
        );

        if (filteredValue.length !== value.length) {
            onChange(filteredValue);
        }
        
    }, [value?.length, buildingOptions?.length]);

    const filteredBuildingOptions = useMemo(() => {
        if (!filterSameCampus) {
            return buildingOptions || [];
        }

        const selectedCampusIds = value
            .map((building) => building.campus_id)
            .filter(Boolean);

        return (buildingOptions || []).filter((building) => {
            return (
                !selectedCampusIds.includes(building.campus_id) ||
                value.some((selectedBuilding) => selectedBuilding.value === building.value)
            );
        });
    }, [buildingOptions, value, filterSameCampus]);

    return (
        <MultiSelectDropdown
            name={name}
            label={label}
            value={value}
            options={filteredBuildingOptions}
            onChange={onChange}
            required={required}
            col_md={col_md}
            col_lg={col_lg}
            placeholder={isLoading ? "Loading buildings..." : "Select Buildings"}
            isDisabled={isDisabled ?? (isLoading || !filteredBuildingOptions?.length)}
            isLoading={isLoading}
            info={false}
            isInvalid={isInvalid}
            error={error}
            selectStyles={selectStyles}
            wrapperClass={wrapperClass}
        />
    );
};

export default CampusBuildingMultiSelect;